import React from "react"

import { Link, useLocation } from "react-router-dom"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import type { AvailableTextTypography } from "@front-shared/types/elements.types"

import { GENERAL_CONFIG } from "../config/general.config"
import { ProduktlyHelper } from "../helpers/produktly.helper"
import { useHasPermissions } from "../hooks/hasPermissions.hook"
import type { IMenuItem } from "../types/objects.types"

export const MenuItemElement: React.FunctionComponent<
    IMenuItemElementProps
> = props => {
    const location = useLocation()

    const canAccessComingSoon = useHasPermissions(
        OneVerifyPermission.useComingSoonFeatures,
        undefined,
        false,
    )

    const isActive = props.item.url === location.pathname
    const isComingSoon = props.item.isComingSoon && !canAccessComingSoon

    const typography = isActive
        ? props.activeTypography || props.typography
        : props.typography

    return (
        <Link
            to={
                !isComingSoon
                    ? props.item.url
                    : `mailto:${GENERAL_CONFIG.supportEmail}`
            }
            target={props.item.isNewTab ? "_blank" : undefined}
            rel={props.item.isNewTab ? "noopener noreferrer" : undefined}
            onClick={props.onClick}
            data-produktly={`nav-${ProduktlyHelper.formatId(props.item.label)}`}
            className={`
                !no-underline
                ${isActive ? "pointer-events-none" : ""}
            `}
        >
            <PrimaryText
                className={`
                    relative
                    ${props.className}
                    ${isActive ? props.activeClassName : props.inactiveClassName}
                    ${
                        isActive
                            ? "cursor-default"
                            : isComingSoon
                              ? "cursor-help"
                              : "cursor-pointer"
                    }
                `}
                centered={props.centered}
                typography={typography}
            >
                {props.item.label}

                {isComingSoon ? (
                    <PrimaryText
                        className={`
                            w-[85px] bg-brand-vibrantPurple pt-[5px] pb-[3px] rounded-[4px]
                            ${props.floatingComingSoon ? "absolute top-[-18px] left-0" : "mt-[4px]"}
                        `}
                        size={12}
                        lineHeight={8}
                        centered
                        color={NIRVANA_COLORS.brand.white}
                    >
                        Coming soon
                    </PrimaryText>
                ) : null}
            </PrimaryText>
        </Link>
    )
}

interface IMenuItemElementProps {
    item: IMenuItem
    className?: string
    activeClassName?: string
    inactiveClassName?: string
    centered?: boolean
    onClick?: () => void
    typography: AvailableTextTypography
    activeTypography?: AvailableTextTypography
    floatingComingSoon?: boolean
}
